// 企業家戶管理人交接 / 受控接管（organization-site §2.1；vms-web IAM-09）：
// 明示新管理人會取得這個家戶的影像與管理權、原管理人會失去哪些權限；理由必填；伺服器要 5 分鐘內重新驗證（會跳對話框）。
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApp } from '../../lib/AppContext';
import { isOrgAdmin, orgApi, type MyOrg, type OrgHome } from '../../lib/planeApi';
import { Dialog } from '../../components/Dialog';
import { memberName } from '../people/MembersTab';
import { errText } from './OrgSection';

function newKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function OwnerTransferDialog({ org, home, close }: { org: MyOrg; home: OrgHome | null; close(): void }) {
  const { t } = useTranslation();
  const { session } = useApp();
  const qc = useQueryClient();
  const me = session?.user?.user_id ?? '';
  const members = useQuery({ queryKey: ['org-members', org.org_id], queryFn: () => orgApi.members(org), enabled: !!home });
  const iOwn = !!home && home.owner_user_id === me;
  const [target, setTarget] = useState('');
  const [reason, setReason] = useState('');
  const [key, setKey] = useState(newKey);
  const [forHome, setForHome] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  if (home && home.home_id !== forHome) {
    setForHome(home.home_id);
    setTarget('');
    setReason('');
    setKey(newKey());
    setErr(null);
    setDone(null);
  }
  const mode: 'handover' | 'takeover' = iOwn ? 'handover' : 'takeover';
  const canAct = iOwn || isOrgAdmin(org);
  const owner = (members.data ?? []).find((m) => m.user_id === home?.owner_user_id);
  const submit = useMutation({
    mutationFn: () => orgApi.ownerTransfer(org, home!.home_id, { mode, target_user_id: target, expected_version: home!.version, reason: reason.trim() }, key),
    onSuccess: (r) => {
      setDone(r.unchanged ? t('transfer.unchanged') : t('transfer.done', { n: r.revoked_grants }));
      void qc.invalidateQueries({ queryKey: ['org-homes', org.org_id] });
      void qc.invalidateQueries({ queryKey: ['me-devices'] });
    },
    onError: (e) => setErr(errText(t, e)),
  });
  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    submit.mutate();
  }
  return (
    <Dialog open={!!home} title={t(`transfer.title_${mode}`, { name: home?.display_name ?? '' })} onClose={close}>
      {done ? (
        <div className="form">
          <p className="notice">{done}</p>
          <button className="btn btn-primary" onClick={close}>
            {t('common.done')}
          </button>
        </div>
      ) : !canAct ? (
        <p className="error">{t('transfer.not_allowed')}</p>
      ) : (
        <form className="form" onSubmit={onSubmit}>
          <p>
            {t('transfer.current')}{t('common.colon')}
            {home?.management_status === 'unassigned' ? <span className="badge badge-warn">{t('transfer.unassigned')}</span> : memberName(owner, home?.owner_user_id ?? '—')}
          </p>
          <label className="field">
            <span>{t('transfer.target')}</span>
            <select className="input" required value={target} onChange={(e) => setTarget(e.target.value)}>
              <option value="">—</option>
              {(members.data ?? [])
                .filter((m) => m.status === 'active' && m.user_id !== home?.owner_user_id)
                .map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {memberName(m)}
                    {m.user_id === me ? ` (${t('transfer.me')})` : ''}
                  </option>
                ))}
            </select>
          </label>
          <div className="notice small">
            <p>{t('transfer.warn_new')}</p>
            {home?.owner_user_id && <p>{t('transfer.warn_old')}</p>}
            {mode === 'takeover' && <p>{t('transfer.warn_takeover')}</p>}
          </div>
          <label className="field">
            <span>{t('transfer.reason')}</span>
            <textarea className="input" required maxLength={500} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          {err && <div className="error">{err}</div>}
          <div className="row-end">
            <span className="small muted">{t('people.stepup_note')}</span>
            <div className="spacer" />
            <button type="button" className="btn" onClick={close}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" disabled={submit.isPending || !target || !reason.trim()}>
              {t(`transfer.submit_${mode}`)}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
